import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import PageHeader from '../../components/PageHeader.jsx';
import Loader, { ErrorBox } from '../../components/Loader.jsx';
import { BusinessTimezoneAPI } from '../../lib/api';
import { notify } from '../../lib/notifications';
import { useAuth } from '../../lib/auth';
import {
  commonTimeZones,
  formatInTimeZone,
  publishOrgTimeZone,
} from '../../lib/displayTimezone.js';

/**
 * Business → Timezone.
 *
 * Org admins pick one IANA zone for the whole organisation. Uni Forms
 * submission times and SignalBridge event-log times both use it.
 */
export default function BusinessTimezoneSettings() {
  const { user, setUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [tz, setTz] = useState('Asia/Kolkata');

  const applyPayload = (payload) => {
    setStatus(payload);
    if (payload && payload.display_timezone) setTz(payload.display_timezone);
  };

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      applyPayload(await BusinessTimezoneAPI.get());
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const save = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = await BusinessTimezoneAPI.update({ display_timezone: tz });
      applyPayload(payload);
      const next = payload.display_timezone || tz;
      const nextUser = user ? { ...user, display_timezone: next } : null;
      if (nextUser) setUser(nextUser);
      publishOrgTimeZone(next, nextUser);
      notify.success(`Timezone saved. Times now show in ${next}.`);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }, [tz, user, setUser]);

  if (loading && !status) return <Loader label="Loading timezone settings…" />;

  const zones = Array.from(new Set([
    tz,
    ...((status && status.common_timezones) || []),
    ...commonTimeZones(),
  ].filter(Boolean)));

  return (
    <>
      <PageHeader
        title="🌐 Timezone"
        subtitle="One timezone for the organisation. Uni Forms submission times and SignalBridge order times both use this setting."
      />

      <ErrorBox error={error} />

      <section className="ty-card">
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 12px' }}>
          Organisation timezone
        </h2>
        <p style={{ color: 'var(--ty-muted)', marginTop: 0, marginBottom: 16 }}>
          Stored times are UTC. Choosing a zone (for example Asia/Kolkata for
          IST) converts them before they are shown in Uni Forms, exports, and
          SignalBridge. Only organisation admins can change this — operators
          cannot pick a personal zone in SignalBridge.
        </p>

        <div className="ty-field">
          <label htmlFor="org-display-timezone">Timezone</label>
          <select
            id="org-display-timezone"
            className="ty-input"
            value={tz}
            onChange={(e) => setTz(e.target.value)}
          >
            {zones.map((z) => (
              <option key={z} value={z}>{z}</option>
            ))}
          </select>
        </div>

        <p style={{ fontSize: 13, color: 'var(--ty-muted)', marginTop: 12 }}>
          Preview: {formatInTimeZone(new Date().toISOString(), tz)}
        </p>

        {status?.preview ? (
          <p style={{ fontSize: 13, color: 'var(--ty-muted)', marginBottom: 0 }}>
            Server clock in this zone: {status.preview}
          </p>
        ) : null}

        <p style={{ fontSize: 13, color: 'var(--ty-muted)', marginBottom: 0 }}>
          SignalBridge settings will follow this value automatically.
          {' '}
          <Link to="/signalbridge/settings">Open SignalBridge settings</Link>
        </p>
      </section>

      <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
        <button
          type="button"
          className="ty-btn"
          onClick={save}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save timezone'}
        </button>
        <button
          type="button"
          className="ty-btn ty-btn--ghost"
          onClick={reload}
          disabled={saving || loading}
        >
          Discard
        </button>
      </div>
    </>
  );
}
